
// BookFlipView_v2.js
// Flipbook shell with: closed->open animation, and page-turn animation while open.
// Props:
//   width, height
//   open, onOpenRequested(), onOpened()
//   onFlipNext()  -> called when user swipes right->left while open
//   onFlipPrev()  -> called when user swipes left->right while open
//
// No extra libraries. Works on RN Web + RN mobile.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Animated, PanResponder, Platform } from 'react-native';

export default function BookFlipView({
  width = 640,
  height = 420,
  open = false,
  onOpenRequested = () => {},
  onOpened = () => {},
  onFlipNext = () => {},
  onFlipPrev = () => {},
  children,
}) {
  const USE_NATIVE = Platform.OS !== 'web';

  // Open animation: 0 closed -> 1 open
  const openAnim = useRef(new Animated.Value(open ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(openAnim, {
      toValue: open ? 1 : 0,
      duration: open ? 520 : 360,
      useNativeDriver: USE_NATIVE,
    }).start(({ finished }) => {
      if (finished && open) onOpened();
    });
  }, [open]);

  // Page-turn animation (while open)
  // flipAnim: 0 idle, 1 completed; flipDir: 'next' (right page turns) or 'prev' (left page turns)
  const [flipDir, setFlipDir] = useState(null); // 'next' | 'prev' | null
  const flipAnim = useRef(new Animated.Value(0)).current;

  // Trigger a page turn overlay animation
  function triggerFlip(dir) {
    if (flipDir) return; // ignore if animating
    setFlipDir(dir);
    flipAnim.setValue(0);
    Animated.timing(flipAnim, { toValue: 1, duration: 450, useNativeDriver: USE_NATIVE }).start(() => {
      setFlipDir(null);
      flipAnim.setValue(0);
    });
    // Call content change a bit after start so the overlay hides the swap
    setTimeout(() => {
      if (dir === 'next') onFlipNext();
      else onFlipPrev();
    }, 200);
  }

  // Swipe gesture: closed -> request open; open -> next/prev
  const pan = useRef(new Animated.Value(0)).current;
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dx) > 12 && Math.abs(g.dx) > Math.abs(g.dy),
        onPanResponderMove: Animated.event([null, { dx: pan }], { useNativeDriver: false }),
        onPanResponderRelease: (_e, g) => {
          if (!open) {
            if (g.dx < -40) onOpenRequested();
          } else {
            if (g.dx < -40) triggerFlip('next'); // right->left
            else if (g.dx > 40) triggerFlip('prev'); // left->right
          }
          Animated.spring(pan, { toValue: 0, useNativeDriver: false }).start();
        },
      }),
    [open, flipDir]
  );

  // Derived styles for cover
  const coverRotateY = openAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '-160deg'] });
  const coverZIndex = openAnim.interpolate({ inputRange: [0, 0.99, 1], outputRange: [10, 1, 0] });

  // Book sizes
  const pageW = width / 2;
  const pageH = height;

  // Flip overlay styles
  const rightFlipRotateY = flipAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '-180deg'] });
  const leftFlipRotateY  = flipAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] });
  const flipOpacity = flipAnim.interpolate({ inputRange: [0, 1], outputRange: [0.9, 0] });

  return (
    <View style={{ alignItems: 'center', justifyContent: 'center' }}>
      <View
        {...panResponder.panHandlers}
        style={{
          width,
          height,
          perspective: 1000,
          backgroundColor: '#E7DDBF',
          borderRadius: 12,
          borderWidth: 1,
          borderColor: '#D8CDAA',
          overflow: 'hidden',
          shadowColor: '#000',
          shadowOpacity: 0.1,
          shadowRadius: 12,
          shadowOffset: { width: 0, height: 6 },
        }}
      >
        {/* Open-page background */}
        <View style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, flexDirection: 'row' }}>
          <View style={{ width: pageW, height: pageH, backgroundColor: '#FCF7E8', borderRightWidth: 1, borderColor: '#E6DBBF' }} />
          <View style={{ width: pageW, height: pageH, backgroundColor: '#FCF7E8' }} />
        </View>

        {/* Spine */}
        <View style={{ position: 'absolute', left: pageW - 2, top: 0, width: 4, height: pageH, backgroundColor: 'black', opacity: 0.05 }} />

        {/* Closed cover */}
        <Animated.View
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width,
            height,
            transformOrigin: 'left center',
            transform: [
              { perspective: 1000 },
              { translateX: -width / 2 + 1 },
              { rotateY: coverRotateY },
              { translateX: width / 2 - 1 },
            ],
            zIndex: coverZIndex,
            backgroundColor: '#D5C8A3',
            borderRightWidth: 2,
            borderColor: '#C2B48D',
          }}
        >
          <View style={{ position: 'absolute', right: 0, top: 0, width: 24, height: pageH, backgroundColor: '#ECE4CB' }}>
            {Array.from({ length: 15 }).map((_, i) => (
              <View key={i} style={{
                position: 'absolute', left: (i / 15) * 22, top: 0, width: 1, height: pageH,
                backgroundColor: '#D8CFB4', opacity: 0.5
              }} />
            ))}
          </View>
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 22, fontWeight: '800' }}>Bhagavad Gītā</Text>
            <Text style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>Swami Tadatmananda (Arsha Bodha)</Text>
            <Text style={{ fontSize: 11, marginTop: 2, opacity: 0.6 }}>Swipe right → left to open</Text>
          </View>
        </Animated.View>

        {/* Foreground content (open) */}
        <View style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
          {children}
        </View>

        {/* Page-turn overlay for NEXT (right page flipping) */}
        {flipDir === 'next' && (
          <Animated.View style={{
            position: 'absolute', left: width/2, top: 0, width: width/2, height: height,
            backgroundColor: '#FDF7E8',
            borderLeftWidth: 1, borderColor: '#E6DBBF',
            transformOrigin: 'left center',
            transform: [{ perspective: 1000 }, { rotateY: rightFlipRotateY }],
            shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 12, shadowOffset: { width: -6, height: 4 },
            opacity: flipOpacity,
          }} />
        )}

        {/* Page-turn overlay for PREV (left page flipping) */}
        {flipDir === 'prev' && (
          <Animated.View style={{
            position: 'absolute', left: 0, top: 0, width: width/2, height: height,
            backgroundColor: '#FDF7E8',
            borderRightWidth: 1, borderColor: '#E6DBBF',
            transformOrigin: 'right center',
            transform: [{ perspective: 1000 }, { rotateY: leftFlipRotateY }],
            shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 12, shadowOffset: { width: 6, height: 4 },
            opacity: flipOpacity,
          }} />
        )}
      </View>
    </View>
  );
}
